import React, { useMemo } from 'react';
import { Box, Button, Typography } from '@mui/material';
import {
  manufacturerFilterState,
  toggleManufacturerFilter,
  type ModelFilterGroup,
} from '../../utils/deviceModelFilters';

const chipSx = (active: boolean, opts?: { bold?: boolean; partial?: boolean }) => ({
  fontSize: opts?.bold ? '0.8rem' : '0.75rem',
  fontWeight: opts?.bold ? 700 : 400,
  padding: '2px 8px',
  minWidth: 'auto',
  backgroundColor: active ? 'rgba(35, 145, 255, 0.1)' : 'transparent',
  color: active || opts?.partial ? '#2391FF' : '#fff',
  borderColor: active || opts?.partial ? '#2391FF' : '#555',
  borderStyle: opts?.partial && !active ? 'dashed' : 'solid',
  '&:hover': {
    backgroundColor: 'rgba(35, 145, 255, 0.05)',
  },
});

type Cluster =
  | { type: 'multi'; group: ModelFilterGroup }
  | { type: 'singles'; groups: ModelFilterGroup[] };

function clusterGroups(groups: ModelFilterGroup[]): Cluster[] {
  const clusters: Cluster[] = [];
  groups.forEach((group) => {
    if (group.models.length <= 1) {
      const last = clusters[clusters.length - 1];
      if (last?.type === 'singles') {
        last.groups.push(group);
      } else {
        clusters.push({ type: 'singles', groups: [group] });
      }
      return;
    }
    clusters.push({ type: 'multi', group });
  });
  return clusters;
}

interface ModelFilterByManufacturerProps {
  title: string;
  groups: ModelFilterGroup[];
  selected: string[];
  onChange: (next: string[]) => void;
}

const ModelFilterByManufacturer: React.FC<ModelFilterByManufacturerProps> = ({
  title,
  groups,
  selected,
  onChange,
}) => {
  const clusters = useMemo(() => clusterGroups(groups), [groups]);

  const toggleModel = (modelo: string) => {
    if (selected.includes(modelo)) {
      onChange(selected.filter((m) => m !== modelo));
    } else {
      onChange([...selected, modelo]);
    }
  };

  const renderModelChip = (modelo: string) => {
    const active = selected.includes(modelo);
    return (
      <Button
        key={modelo}
        variant={active ? 'contained' : 'outlined'}
        size="small"
        onClick={() => toggleModel(modelo)}
        sx={chipSx(active)}
      >
        {modelo}
      </Button>
    );
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography sx={{ color: '#2391FF', fontSize: '14px', fontWeight: 'bold', mb: 1 }}>
        {title}
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {clusters.map((cluster) => {
          if (cluster.type === 'singles') {
            const keys = cluster.groups.map((g) => g.manufacturer).join('-');
            return (
              <Box key={keys} sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {cluster.groups.map((group) => {
                  const modelo = group.models[0];
                  return modelo ? renderModelChip(modelo) : null;
                })}
              </Box>
            );
          }

          const { group } = cluster;
          const brandState = manufacturerFilterState(selected, group.models);
          const brandAll = brandState === 'all';
          const brandPartial = brandState === 'partial';
          return (
            <Box key={group.manufacturer}>
              <Button
                variant={brandAll ? 'contained' : 'outlined'}
                size="small"
                onClick={() => onChange(toggleManufacturerFilter(selected, group.models))}
                aria-pressed={brandAll}
                aria-label={`Filtrar todos los modelos ${group.manufacturer}`}
                sx={{ ...chipSx(brandAll, { bold: true, partial: brandPartial }), mb: 1 }}
              >
                {group.manufacturer}
              </Button>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {group.models.map((modelo) => renderModelChip(modelo))}
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default ModelFilterByManufacturer;
